// src/features/developers/components/ReferenceTab.tsx
//
// The interactive reference. Mounted once and kept (hidden, never unmounted) while the reader
// visits other tabs; the viewer is loaded the first time this tab is open, never before.
import React, { useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';
import { Alert, Box, Typography, useTheme } from '@mui/material';
import { loadReferenceViewer, mountReference } from '../scalar';

interface ReferenceTabProps {
  active: boolean;
  spec: unknown;
  title: string;
}

export const ReferenceTab: React.FC<ReferenceTabProps> = ({ active, spec, title }) => {
  const translate = useTranslate();
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const host = useRef<HTMLDivElement | null>(null);
  const instance = useRef<{ destroy?: () => void } | null>(null);
  const mountedDark = useRef<boolean | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!active || !spec || !host.current) return;
    if (instance.current && mountedDark.current === dark) return;
    let alive = true;
    loadReferenceViewer()
      .then(() => {
        if (!alive || !host.current) return;
        instance.current?.destroy?.();
        host.current.innerHTML = '';
        instance.current = mountReference(host.current, spec, { dark, title });
        mountedDark.current = dark;
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [active, spec, dark, title]);

  useEffect(
    () => () => {
      instance.current?.destroy?.();
      instance.current = null;
    },
    []
  );

  return (
    <Box>
      {failed && (
        <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
          {translate('developers.errors.viewer')}
        </Alert>
      )}
      {!spec && !failed && <Typography sx={{ color: theme.palette.text.secondary }}>{translate('developers.reference.loading')}</Typography>}
      <Box
        ref={host}
        sx={{
          border: spec ? `1px solid ${theme.palette.divider}` : 0,
          borderRadius: theme.custom.radius.card,
          overflow: 'hidden',
          minHeight: spec ? 'calc(100vh - 260px)' : 0,
          backgroundColor: theme.palette.background.paper,
          maxWidth: '100%',
        }}
      />
    </Box>
  );
};
